import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import { checksApi, customersApi, vendorsApi } from '../../api';
import { Check, Customer, Vendor } from '../../types';
import { formatCurrency, formatDate } from '../../utils';
import { useDataTableState, useAccountMenuItem } from '../../hooks';

// Helper component for column headers with help tooltips
const ColumnHeader: React.FC<{ label: string; tooltip: string; id: string }> = ({ label, tooltip, id }) => (
  <>
    <Tooltip target={`#${id}`} position="top" />
    <span>{label}{' '}</span>
      <i
        id={id}
        className="ms-out ms-out-help-outline icon-help"
        data-pr-tooltip={tooltip}
      />
  </>
);

type CheckFilter = 'all' | 'missing_customer' | 'missing_vendor' | 'payee_is_customer';

const FILTER_OPTIONS = [
  { label: 'All Checks', value: 'all' },
  { label: 'Missing Customer', value: 'missing_customer' },
  { label: 'Missing Payee', value: 'missing_vendor' },
  { label: 'Customer is the Payee', value: 'payee_is_customer' },
];

const FILTER_STORAGE_KEY = 'customerIdentification:reviewChecks:filter';

interface ReviewChecksTabProps {
  clientId: string;
}

export const ReviewChecksTab: React.FC<ReviewChecksTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const [allChecks, setAllChecks] = useState<Check[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { accountMenuItem, selectedAccountId } = useAccountMenuItem(clientId, { showAllOption: true });
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<Check | null>(null);
  const [customerInput, setCustomerInput] = useState<Customer | string | null>(null);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);

  // Vendor/Payee assignment state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [vendorInput, setVendorInput] = useState<Vendor | string | null>(null);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [payeeIsCustomer, setPayeeIsCustomer] = useState(false);

  // Filter state with localStorage persistence
  const [activeFilter, setActiveFilter] = useState<CheckFilter>(() => {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    return (stored as CheckFilter) || 'all';
  });

  // Persistent sort state
  const { sortField, sortOrder, handleSort } = useDataTableState({
    storageKey: 'customerIdentification:reviewChecks',
    defaultSort: { field: 'check_date', order: -1 },
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [checksData, customersData, vendorsData] = await Promise.all([
        checksApi.listApprovedChecks(clientId, undefined, selectedAccountId ?? undefined),
        customersApi.listCustomers(clientId),
        vendorsApi.getVendors(clientId),
      ]);

      setAllChecks(checksData);
      setCustomers(customersData);
      setVendors(vendorsData);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to load data',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [clientId, selectedAccountId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Compute counts for each filter
  const filterCounts: Record<CheckFilter, number> = {
    all: allChecks.length,
    missing_customer: allChecks.filter((c) => !c.customer_id).length,
    missing_vendor: allChecks.filter((c) => !c.vendor_id && !c.payee_is_customer).length,
    payee_is_customer: allChecks.filter((c) => c.payee_is_customer).length,
  };

  // Apply filter to checks
  const checks = (() => {
    switch (activeFilter) {
      case 'missing_customer':
        return allChecks.filter((c) => !c.customer_id);
      case 'missing_vendor':
        return allChecks.filter((c) => !c.vendor_id && !c.payee_is_customer);
      case 'payee_is_customer':
        return allChecks.filter((c) => c.payee_is_customer);
      default:
        return allChecks;
    }
  })();

  const handleFilterChange = (value: CheckFilter) => {
    setActiveFilter(value);
    localStorage.setItem(FILTER_STORAGE_KEY, value);
  };

  const handleAssignClick = (check: Check) => {
    setSelectedCheck(check);
    setCustomerInput('');
    setShowAssignDialog(true);
  };

  const searchCustomers = (event: AutoCompleteCompleteEvent) => {
    const query = event.query.toLowerCase();
    const filtered = customers.filter((c) =>
      c.name.toLowerCase().includes(query)
    );
    setFilteredCustomers(filtered);
  };

  const handleConfirmAssign = async () => {
    if (!selectedCheck || !customerInput) return;

    try {
      setIsAssigning(true);

      let customerId: string;
      let customerName: string;

      if (typeof customerInput === 'string') {
        // Create new customer
        const newCustomer = await customersApi.createCustomer(clientId, {
          name: customerInput,
        });
        customerId = newCustomer.id;
        customerName = newCustomer.name;
        setCustomers([...customers, newCustomer]);
      } else {
        // Use existing customer
        customerId = customerInput.id;
        customerName = customerInput.name;
      }

      await checksApi.assignCheckToCustomer(clientId, selectedCheck.id, customerId);
      // Update the check in place with the new customer info
      setAllChecks(allChecks.map((c) =>
        c.id === selectedCheck.id
          ? { ...c, customer_id: customerId, customer_name: customerName }
          : c
      ));
      setShowAssignDialog(false);
      setCustomerInput(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Check assigned to customer',
        life: 3000,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to assign check',
        life: 5000,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  // Vendor/Payee assignment handlers
  const handleAssignVendorClick = (check: Check) => {
    setSelectedCheck(check);
    setVendorInput('');
    setPayeeIsCustomer(check.payee_is_customer || false);
    setShowVendorDialog(true);
  };

  const searchVendors = (event: AutoCompleteCompleteEvent) => {
    const query = event.query.toLowerCase();
    const filtered = vendors.filter((v) =>
      v.name.toLowerCase().includes(query)
    );
    setFilteredVendors(filtered);
  };

  const handleConfirmVendorAssign = async () => {
    if (!selectedCheck) return;

    // If payee is customer, we don't need vendor input
    // If payee is vendor, we need vendor input
    if (!payeeIsCustomer && !vendorInput) return;

    try {
      setIsAssigning(true);

      if (payeeIsCustomer) {
        // Payee is same as customer - just set the flag, use existing customer_id
        await checksApi.assignVendorToCheck(
          clientId,
          selectedCheck.id,
          null,
          true,
          selectedCheck.customer_id
        );
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Payee set to customer',
          life: 3000,
        });
      } else {
        // Assign vendor as payee
        let vendorId: string;

        if (typeof vendorInput === 'string') {
          // Create new vendor
          const newVendor = await vendorsApi.createVendor(clientId, {
            name: vendorInput,
          });
          vendorId = newVendor.id;
          setVendors([...vendors, newVendor]);
        } else {
          // Use existing vendor
          vendorId = (vendorInput as Vendor).id;
        }

        await checksApi.assignVendorToCheck(clientId, selectedCheck.id, vendorId, false, null);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Check assigned to vendor',
          life: 3000,
        });
      }

      // Reload data
      await loadData();

      setShowVendorDialog(false);
      setVendorInput(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to assign payee',
        life: 5000,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const amountBodyTemplate = (rowData: Check) => {
    return formatCurrency(rowData.amount);
  };

  const customerBodyTemplate = (rowData: Check) => {
    return rowData.customer_name || '—';
  };

  const vendorBodyTemplate = (rowData: Check) => {
    if (rowData.payee_is_customer) {
      return 'Same as customer';
    }
    return rowData.vendor_name || '—';
  };

  const actionBodyTemplate = (rowData: Check) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-person-add"
          link
          onClick={() => handleAssignClick(rowData)}
          disabled={isAssigning}
          tooltip="Assign Customer"
          tooltipOptions={{ position: 'bottom' }}
        />
        <Button
          icon="ms-out ms-out-storefront"
          link
          onClick={() => handleAssignVendorClick(rowData)}
          disabled={isAssigning}
          tooltip="Assign Payee"
          tooltipOptions={{ position: 'bottom' }}
        />
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />

      <Menubar
        className="actions-menubar"
        model={[
          accountMenuItem,
          {
            label: `${FILTER_OPTIONS.find((o) => o.value === activeFilter)?.label} (${filterCounts[activeFilter]})`,
            icon: 'ms-out ms-out-filter-alt',
            items: FILTER_OPTIONS.map((option) => ({
              label: `${option.label} (${filterCounts[option.value as CheckFilter]})`,
              command: () => handleFilterChange(option.value as CheckFilter),
            })),
          },
        ]}
      />

      <DataTable
        value={checks}
        dataKey="id"
        loading={isLoading}
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> approved checks
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        emptyMessage="No approved checks"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
      >
        <Column field="check_number" header="#" sortable style={{ width: '8%' }} />
        <Column
          field="check_date"
          header="Date"
          body={(rowData) => formatDate(rowData.check_date)}
          sortable
          style={{ width: '10%' }}
        />
        <Column field="amount" header="Amount" body={amountBodyTemplate} sortable style={{ width: '10%' }} />
        <Column
          field="payee_name"
          header={<ColumnHeader 
            label="Payee"
             tooltip="The payee name as written on the check" 
             id="payee-help" />}
          sortable
          style={{ width: '18%' }}
        />
        <Column
          field="memo"
          header={<ColumnHeader 
            label="Memo" 
            tooltip="The memo field as written on the check" 
            id="memo-help" />}
          style={{ width: '18%' }}
        />
        <Column
          field="customer_name"
          header={<ColumnHeader 
            label="Customer" 
            tooltip="Customer this check is associated with (syncs to QB)" 
            id="customer-help" />}
          body={customerBodyTemplate}
          sortable
          style={{ width: '15%' }}
        />
        <Column
          field="vendor_name"
          header={<ColumnHeader 
            label="Payee" 
            tooltip="Vendor or customer who received payment (syncs to QB)" 
            id="vendor-help" />}
          body={vendorBodyTemplate}
          sortable
          style={{ width: '15%' }}
        />
        <Column body={actionBodyTemplate}/>
      </DataTable>

      {/* Assign to Customer Dialog */}
      <Dialog
        header="Assign Check to Customer"
        visible={showAssignDialog}
        className="customer-assignment-dialog"
        onHide={() => setShowAssignDialog(false)}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowAssignDialog(false)}
              severity="secondary"
            />
            <Button
              label="Assign"
              severity="success"
              onClick={handleConfirmAssign}
              loading={isAssigning}
              disabled={!customerInput}
            />
          </div>
        }
      >
        <div>
          <div className="dialog-context-info">
            <div className="context-row">
              <span className="context-label">Payee:</span> {selectedCheck?.payee_name || '—'}
            </div>
            {selectedCheck?.memo && (
              <div className="context-row">
                <span className="context-label">Memo:</span> {selectedCheck.memo}
              </div>
            )}
          </div>

          <div className="customer-assignment-form">
            <div className="customer-assignment-label-row">
              <label className="customer-assignment-label">Customer</label>
              <Button
                label="+ New"
                link
                size="small"
                onClick={() => {
                  if (selectedCheck?.payee_name) {
                    setCustomerInput(selectedCheck.payee_name);
                  }
                }}
              />
            </div>
            <AutoComplete
              value={customerInput}
              suggestions={filteredCustomers}
              completeMethod={searchCustomers}
              field="name"
              onChange={(e) => setCustomerInput(e.value)}
              placeholder="Select or type new customer name"
              className="customer-assignment-input"
              dropdown
            />
          </div>
        </div>
      </Dialog>

      {/* Assign to Vendor/Customer Dialog */}
      <Dialog
        header="Assign Check Payee"
        visible={showVendorDialog}
        className="customer-assignment-dialog"
        onHide={() => setShowVendorDialog(false)}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowVendorDialog(false)}
              severity="secondary"
            />
            <Button
              label="Assign"
              severity="success"
              onClick={handleConfirmVendorAssign}
              loading={isAssigning}
              disabled={!payeeIsCustomer && !vendorInput}
            />
          </div>
        }
      >
        <div>
          <div className="dialog-context-info">
            <div className="context-row">
              <span className="context-label">Payee:</span> {selectedCheck?.payee_name || '—'}
            </div>
            {selectedCheck?.memo && (
              <div className="context-row">
                <span className="context-label">Memo:</span> {selectedCheck.memo}
              </div>
            )}
          </div>

          <div className="dialog-checkbox-row">
            <Checkbox
              inputId="payeeIsCustomer"
              checked={payeeIsCustomer}
              onChange={(e: CheckboxChangeEvent) => setPayeeIsCustomer(e.checked ?? false)}
              disabled={!selectedCheck?.customer_id}
            />
            <label htmlFor="payeeIsCustomer">
              Payee is same as customer
              {selectedCheck?.customer_name && ` (${selectedCheck.customer_name})`}
            </label>
          </div>

          {!payeeIsCustomer && (
            <>
              <div className="customer-assignment-label-row">
                <label className="customer-assignment-label">Vendor</label>
                <Button
                  label="+ New"
                  link
                  size="small"
                  onClick={() => {
                    if (selectedCheck?.payee_name) {
                      setVendorInput(selectedCheck.payee_name);
                    }
                  }}
                />
              </div>
              <AutoComplete
                value={vendorInput}
                suggestions={filteredVendors}
                completeMethod={searchVendors}
                field="name"
                onChange={(e) => setVendorInput(e.value)}
                placeholder="Select or type new vendor name"
                className="customer-assignment-input"
                dropdown
              />
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};
